import React, { useState } from "react";
import classNames from "classnames";
import { Card, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faPhone, 
  faEnvelope, 
  faComment, 
  faCopy,
  faEdit,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import Badge from "components/base/Badge";
import { getInitials } from "helpers/utils";
import toast from "react-hot-toast";

const LeadProfileCard = ({
  className,
  leadDetails,
}: {
  className?: string;
  leadDetails?: any;
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Safe extraction of values
  const leadType = leadDetails?.lead_type?.lead_type || null;
  const leadStatus = leadDetails?.lead_status?.status || null;

  const copyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      toast.success(`${fieldName} copied to clipboard!`);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const navigateToCommunication = (action?: string) => {
    const queryParams = new URLSearchParams({
      type: action || 'call',
    });

    navigate(`${location.pathname}?${queryParams.toString()}`);
  };

  const initiateCall = () => {
    if (!leadDetails?.lead_phone) {
      toast.error('No phone number available');
      return;
    }
    navigateToCommunication('call');
  };

  const composeEmail = () => {
    if (!leadDetails?.lead_email) {
      toast.error('No email address available');
      return;
    }
    navigateToCommunication('emails');
  };

  const sendMessage = () => {
    if (!leadDetails?.lead_phone) {
      toast.error('No phone number available');
      return;
    }
    navigateToCommunication('call');
  };

  const leadName = `${leadDetails?.lead_first_name || ''} ${leadDetails?.lead_last_name || ''}`.trim();
  const initials = getInitials(leadDetails?.lead_first_name, leadDetails?.lead_last_name);

  return (
    <div className={classNames('modern-lead-profile-card', className)}>
      <Card className="lead-profile-card border-0">
        <Card.Body className="p-4">
          {/* Header Section */}
          <div className="profile-header mb-4">
            <div className="d-flex align-items-start gap-3">
              {/* Enhanced Avatar */}
              <div className="profile-avatar-wrapper">
                <div className="profile-avatar">
                  <span className="avatar-text">{initials}</span>
                  <div className="avatar-ring"></div>
                </div>
                <div className="status-indicator online"></div>
              </div>

              {/* Contact Info */}
              <div className="profile-info flex-grow-1">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h3 className="profile-name mb-0">{leadName || 'Unknown Lead'}</h3>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="edit-btn p-1"
                    title="Edit Contact"
                    as={Link}
                    to={`/edit-lead/${leadDetails?.id}`}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                </div>

                {/* Status Badges - Now directly under name */}
                <div className="status-badges mb-3">
                  {leadType && (
                    <Badge variant="phoenix" bg="success" className="me-2 status-badge">
                      <span className="badge-dot"></span>
                      {leadType}
                    </Badge>
                  )}
                  {leadStatus && (
                    <Badge variant="phoenix" bg="warning" className="me-2 status-badge">
                      <span className="badge-dot"></span>
                      {leadStatus}
                    </Badge>
                  )}
                </div>

                {/* Contact Methods - Now below status badges */}
                <div className="contact-methods mb-3">
                  {leadDetails?.lead_email && (
                    <div className="contact-item">
                      <div className="contact-icon">
                        <FontAwesomeIcon icon={faEnvelope} />
                      </div>
                      <div className="contact-details">
                        <Link 
                          to={`mailto:${leadDetails.lead_email}`}
                          className="contact-link"
                        >
                          {leadDetails.lead_email}
                        </Link>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Copy email</Tooltip>}
                        >
                          <Button
                            variant="link"
                            size="sm"
                            className="copy-btn p-0 ms-2"
                            onClick={() => copyToClipboard(leadDetails.lead_email, 'Email')}
                          >
                            <FontAwesomeIcon 
                              icon={copiedField === 'Email' ? faCheck : faCopy} 
                              className={copiedField === 'Email' ? 'text-success' : ''} 
                            />
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </div>
                  )}

                  {leadDetails?.lead_phone && (
                    <div className="contact-item">
                      <div className="contact-icon">
                        <FontAwesomeIcon icon={faPhone} />
                      </div>
                      <div className="contact-details">
                        <Link 
                          to={`tel:${leadDetails.lead_phone}`}
                          className="contact-link"
                        >
                          {leadDetails.lead_phone}
                        </Link>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Copy phone number</Tooltip>}
                        >
                          <Button
                            variant="link"
                            size="sm"
                            className="copy-btn p-0 ms-2"
                            onClick={() => copyToClipboard(leadDetails.lead_phone, 'Phone')}
                          >
                            <FontAwesomeIcon 
                              icon={copiedField === 'Phone' ? faCheck : faCopy}
                              className={copiedField === 'Phone' ? 'text-success' : ''} 
                            />
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions mb-3">
            <div className="action-buttons">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Open Call Interface</Tooltip>}
              >
                <Button 
                  variant="success" 
                  size="sm" 
                  className="action-btn"
                  onClick={initiateCall}
                  disabled={!leadDetails?.lead_phone}
                >
                  <FontAwesomeIcon icon={faPhone} />
                  Call
                </Button>
              </OverlayTrigger>

              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Open Email Interface</Tooltip>}
              >
                <Button 
                  variant="primary" 
                  size="sm" 
                  className="action-btn"
                  onClick={composeEmail}
                  disabled={!leadDetails?.lead_email}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                  Email
                </Button>
              </OverlayTrigger>

              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Open Messaging Interface</Tooltip>}
              >
                <Button 
                  variant="info" 
                  size="sm" 
                  className="action-btn"
                  onClick={sendMessage}
                  disabled={!leadDetails?.lead_phone}
                >
                  <FontAwesomeIcon icon={faComment} />
                  Text
                </Button>
              </OverlayTrigger>
            </div>
          </div>

          {/* Tags Section */}
          {leadDetails?.lead_tags && Array.isArray(leadDetails.lead_tags) && leadDetails.lead_tags.length > 0 && (
            <div className="tags-section">
              <h6 className="tags-title mb-2">Tags</h6>
              <div className="tags-container">
                {leadDetails.lead_tags.map((item: any, index: number) => {
                  // Defensive normalization — `item.tags` has historically
                  // come back as either a string or an object wrapper.
                  // Rendering the object directly throws
                  // "Objects are not valid as a React child".
                  const label =
                    typeof item === 'string' ? item
                    : typeof item?.tags === 'string' ? item.tags
                    : typeof item?.tag === 'string' ? item.tag
                    : typeof item?.name === 'string' ? item.name
                    : null;
                  if (!label) return null;
                  return (
                    <Badge
                      key={index}
                      variant="tag"
                      className="tag-badge me-2 mb-2"
                    >
                      #{label}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Enhanced Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .modern-lead-profile-card {
          --profile-primary: #0d6efd;
          --profile-success: #198754;
          --profile-info: #0dcaf0;
          --profile-warning: #ffc107;
          --profile-secondary: #6c757d;
          --profile-light: #f8f9fa;
          --profile-dark: #212529;
          --profile-border: #e9ecef;
          --profile-shadow: 0 4px 20px rgba(0,0,0,0.08);
          --profile-shadow-hover: 0 6px 25px rgba(0,0,0,0.12);
        }

        .lead-profile-card {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border-radius: 16px !important;
          transition: all 0.3s ease;
          overflow: hidden;
          position: relative;
        }

        .lead-profile-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--profile-primary), var(--profile-success), var(--profile-info));
        }

        .lead-profile-card:hover {
          box-shadow: var(--profile-shadow-hover);
          transform: translateY(-2px);
        }

        .profile-avatar-wrapper {
          position: relative;
          flex-shrink: 0;
        }

        .profile-avatar {
          width: 72px;
          height: 72px;
          background: linear-gradient(135deg, var(--profile-primary), var(--profile-info));
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          box-shadow: 0 4px 12px rgba(13, 110, 253, 0.25);
        }

        .avatar-text {
          color: white;
          font-size: 1.5rem;
          font-weight: 600;
          text-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }

        .avatar-ring {
          position: absolute;
          inset: -3px;
          border-radius: 21px;
          background: linear-gradient(135deg, var(--profile-primary), var(--profile-info));
          z-index: -1;
          opacity: 0.3;
        }

        .status-indicator {
          position: absolute;
          bottom: 4px;
          right: 4px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .status-indicator.online {
          background: #22c55e;
        }

        .profile-name {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--profile-dark);
          line-height: 1.2;
        }

        .edit-btn {
          color: var(--profile-secondary) !important;
          transition: all 0.2s ease;
          border-radius: 8px;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .edit-btn:hover {
          color: var(--profile-primary) !important;
          background: var(--profile-light);
        }

        .contact-methods {
          space-y: 0.75rem;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .contact-icon {
          width: 28px;
          height: 28px;
          background: var(--profile-light);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--profile-secondary);
          flex-shrink: 0;
          transition: all 0.2s ease;
        }

        .contact-item:hover .contact-icon {
          background: var(--profile-primary);
          color: white;
          transform: scale(1.05);
        }

        .contact-details {
          display: flex;
          align-items: center;
          flex-grow: 1;
          min-width: 0;
        }

        .contact-link {
          color: var(--profile-dark);
          text-decoration: none;
          font-weight: 500;
          font-size: 0.85rem;
          transition: all 0.2s ease;
          word-break: break-word;
          overflow-wrap: break-word;
          max-width: 280px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex-shrink: 1;
        }

        .contact-link:hover {
          color: var(--profile-primary);
          text-decoration: none;
        }

        .copy-btn {
          color: var(--profile-secondary) !important;
          font-size: 0.8rem;
          transition: all 0.2s ease;
          opacity: 0.7;
        }

        .copy-btn:hover {
          color: var(--profile-primary) !important;
          opacity: 1;
          transform: scale(1.1);
        }

        .status-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .status-badge {
          display: flex !important;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem !important;
          border-radius: 12px !important;
          font-weight: 500 !important;
          font-size: 0.6rem !important;
        }

        .badge-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: currentColor;
          opacity: 0.8;
        }

        .quick-actions {
          border-top: 1px solid var(--profile-border);
          padding-top: 1rem;
        }

        .action-buttons {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
          max-width: 300px;
          margin: 0 auto;
        }

        .action-btn {
          display: flex !important;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.6rem 0.75rem !important;
          border-radius: 10px !important;
          font-weight: 500 !important;
          font-size: 0.8rem !important;
          transition: all 0.2s ease !important;
          border: none !important;
          white-space: nowrap;
        }

        .action-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .action-btn:disabled {
          opacity: 0.5;
          transform: none !important;
          box-shadow: none !important;
        }

        .tags-section {
          border-top: 1px solid var(--profile-border);
          padding-top: 1rem;
        }

        .tags-title {
          color: var(--profile-secondary);
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .tags-container {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .tag-badge {
          background: var(--profile-light) !important;
          color: var(--profile-secondary) !important;
          border: 1px solid var(--profile-border) !important;
          padding: 0.4rem 0.7rem !important;
          border-radius: 8px !important;
          font-size: 0.75rem !important;
          font-weight: 500 !important;
          transition: all 0.2s ease;
        }

        .tag-badge:hover {
          background: var(--profile-primary) !important;
          color: white !important;
          border-color: var(--profile-primary) !important;
          transform: scale(1.05);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .profile-avatar {
            width: 60px;
            height: 60px;
            border-radius: 15px;
          }

          .avatar-text {
            font-size: 1.3rem;
          }

          .profile-name {
            font-size: 1.2rem;
          }

          .action-buttons {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.75rem;
          }

          .action-btn {
            padding: 0.7rem 1rem !important;
          }
        }

        /* Animation for badges */
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .status-badge, .tag-badge {
          animation: slideInUp 0.3s ease forwards;
        }
        `
      }} />
    </div>
  );
};

export default LeadProfileCard;
